import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { resolveMedia, isPdfUrl } from "@/lib/api";
import { ArrowLeft, CalendarDays, FileText, ExternalLink } from "lucide-react";
import MediaGallery from "@/components/MediaGallery";
import RichText from "@/components/RichText";
import useSEO, { stripHtml, injectJsonLd, removeJsonLd } from "@/lib/seo";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; }
}

export default function NewsDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/news/${slug}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
    api.get("/news", { params: { status: "published", limit: 4 } }).then((r) => setRelated((r.data || []).filter((n) => n.slug !== slug).slice(0, 3)));
  }, [slug]);

  // Compute SEO inputs from the article (or fall back to slug while loading)
  const teaser = item?.teaser?.trim();
  const bodyText = item ? stripHtml(item.body, 240) : "";
  const description = teaser || bodyText || undefined;
  const articleImage = item?.image_preview_url && isPdfUrl(item?.image_url || "")
    ? `https://baustelleninfo.com${item.image_preview_url}`
    : item?.image_url && !isPdfUrl(item.image_url)
      ? `https://baustelleninfo.com${item.image_url.startsWith("/") ? item.image_url : "/" + item.image_url}`
      : undefined;
  useSEO({
    title: item?.title || (notFound ? "News nicht gefunden" : "News"),
    description,
    path: `/news/${slug}`,
    image: articleImage,
    type: "article",
  });

  // JSON-LD Article structured data
  useEffect(() => {
    if (!item) return;
    injectJsonLd("news-article-jsonld", {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: item.title,
      description: description || item.title,
      image: articleImage ? [articleImage] : undefined,
      datePublished: item.date,
      dateModified: item.updated_at || item.date,
      author: item.author ? { "@type": "Organization", name: item.author } : { "@type": "Organization", name: "Stadt Detmold" },
      publisher: {
        "@type": "Organization",
        name: "Stadt Detmold",
        logo: { "@type": "ImageObject", url: "https://baustelleninfo.com/icon-512.png" },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://baustelleninfo.com/news/${item.slug}`,
      },
      inLanguage: "de-DE",
    });
    return () => removeJsonLd("news-article-jsonld");
  }, [item, description, articleImage]);

  if (notFound) return (
    <div className="container-narrow py-24 text-center" data-testid="news-not-found">
      <h1 className="font-semibold text-3xl mb-4">News nicht gefunden</h1>
      <Link to="/news" className="text-[hsl(var(--brand-green-strong))] hover:underline">Zurück zur Übersicht</Link>
    </div>
  );

  if (!item) return <div className="container-narrow py-24 text-neutral-500 text-sm">Lade…</div>;

  const heroIsPdf = item.image_url && isPdfUrl(item.image_url);

  return (
    <article className="bg-white" data-testid="news-detail-page">
      {item.image_url && !heroIsPdf && (
        <div className="w-full aspect-[21/9] bg-neutral-100 overflow-hidden">
          <img src={resolveMedia(item.image_url)} alt={item.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="container-narrow py-16 lg:py-24 max-w-3xl">
        <Link to="/news" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-[hsl(var(--brand-green-strong))] mb-8" data-testid="news-back">
          <ArrowLeft className="h-4 w-4" /> Zur Übersicht
        </Link>
        <p className="text-xs text-[hsl(var(--brand-green-strong))] flex items-center gap-2 mb-3">
          <CalendarDays className="h-3.5 w-3.5" /> {formatDate(item.date)}
          {item.author && <span className="text-neutral-400">· {item.author}</span>}
        </p>
        <h1 className="font-semibold text-4xl sm:text-5xl leading-tight mb-6" data-testid="news-detail-title">{item.title}</h1>
        {item.teaser && <p className="text-lg text-neutral-600 leading-relaxed mb-10">{item.teaser}</p>}
        <div className="prose-quartier text-neutral-700 leading-relaxed">
          <RichText html={item.body} />
        </div>
        {item.image_caption && !heroIsPdf && <p className="mt-8 text-xs text-neutral-400 italic">{item.image_caption}</p>}

        {/* PDF-Anhang als kompakte, harmonische Karte UNTER dem Text */}
        {heroIsPdf && (
          <aside className="mt-12 pt-8 border-t border-brand-line" data-testid="news-detail-attachment">
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-4">Anhang</p>
            <a
              href={resolveMedia(item.image_url)}
              target="_blank"
              rel="noreferrer"
              data-testid="news-detail-attachment-link"
              className="group flex items-stretch gap-4 border border-brand-line hover:border-[hsl(var(--brand-green))] hover:shadow-sm transition-all bg-white overflow-hidden max-w-md"
            >
              <div className="w-24 sm:w-28 aspect-[3/4] flex-shrink-0 bg-neutral-100 overflow-hidden relative">
                {item.image_preview_url ? (
                  <img
                    src={resolveMedia(item.image_preview_url)}
                    alt={item.image_caption || item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-200">
                    <FileText className="h-8 w-8 text-[hsl(var(--brand-green-strong))]" strokeWidth={1.5} />
                  </div>
                )}
                <span className="absolute bottom-1.5 left-1.5 bg-white/95 backdrop-blur-sm text-[9px] uppercase tracking-[0.16em] text-[hsl(var(--brand-green-strong))] px-1.5 py-0.5 font-medium">
                  PDF
                </span>
              </div>
              <div className="flex-1 min-w-0 py-3.5 pr-4 flex flex-col justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-400 mb-1">Dokument</p>
                  <p className="text-sm text-neutral-900 font-medium leading-snug line-clamp-2">
                    {item.image_caption || "PDF zum Download"}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--brand-green-strong))] font-medium mt-2 group-hover:gap-2 transition-all">
                  Öffnen <ExternalLink className="h-3 w-3" />
                </span>
              </div>
            </a>
          </aside>
        )}

        <MediaGallery items={item.media || []} testid="news-media-gallery" />
      </div>

      {related.length > 0 && (
        <section className="border-t border-brand-line bg-neutral-50/70">
          <div className="container-narrow py-16">
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-3">Weitere News</p>
            <h2 className="font-semibold text-2xl mb-8">Das könnte Sie auch interessieren</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((n) => (
                <Link key={n.id} to={`/news/${n.slug}`} className="group block" data-testid={`related-${n.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-100 mb-4 flex items-center justify-center relative">
                    {n.image_url ? (
                      isPdfUrl(n.image_url) ? (
                        n.image_preview_url ? (
                          <>
                            <img src={resolveMedia(n.image_preview_url)} alt={n.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                            <span className="absolute top-2 left-2 bg-[hsl(var(--brand-green-dark))] text-white text-[10px] uppercase tracking-wider px-1.5 py-0.5 inline-flex items-center gap-1"><FileText className="h-2.5 w-2.5" />PDF</span>
                          </>
                        ) : (
                          <div className="flex flex-col items-center justify-center text-neutral-500 gap-1.5 px-3 text-center">
                            <FileText className="h-8 w-8" strokeWidth={1.5} />
                            <span className="text-[10px] uppercase tracking-wider">PDF</span>
                          </div>
                        )
                      ) : (
                        <img src={resolveMedia(n.image_url)} alt={n.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                      )
                    ) : <span className="text-neutral-300 text-xs">Kein Bild</span>}
                  </div>
                  <p className="text-xs text-[hsl(var(--brand-green-strong))]">{formatDate(n.date)}</p>
                  <h3 className="font-semibold text-lg mt-1 group-hover:text-[hsl(var(--brand-green-strong))] transition-colors">{n.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
